/**
 * ACCOUNTS_TABLE is inherently portfolio-wide (there is no "accounts within
 * an account" view) — always sources the GLOBAL portfolio query regardless
 * of the dashboard's own account scope, matching the seeded default layout
 * (this builtin only appears on the GLOBAL "Portfolio" dashboard).
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchPortfolioDashboard } from '@/api/dashboard';
import { WidgetBoundary } from '../WidgetBoundary';
import { AccountsTable } from '../AccountsTable';
import { CardHeader, IconBriefcase } from '../CardHeader';
import { WidgetSkeleton } from '../WidgetSkeleton';
import type { BuiltinHostProps } from './hostProps';

export function AccountsTableHost({ title, minHeight, onRowCount }: BuiltinHostProps) {
  const portfolio = useQuery({
    queryKey: ['portfolio-dashboard'],
    queryFn: fetchPortfolioDashboard,
    staleTime: 30_000,
  });

  // Resolved row count for DashboardGrid's content-driven auto-height — null
  // while loading/erroring (mirrors the JSX gates below) so the pre-data MIN
  // reservation holds until a real fetch resolves, rather than snapping to a
  // premature 0. A plain number dep (not the query object) so the effect
  // fires only when the count itself actually changes.
  const rowCount = !portfolio.isLoading && !portfolio.isError
    ? portfolio.data?.accounts.length ?? 0
    : null;
  useEffect(() => {
    if (rowCount !== null) onRowCount?.(rowCount);
  }, [rowCount, onRowCount]);

  return (
    <WidgetBoundary label={title} minHeight={minHeight}>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader icon={<IconBriefcase />} title={title} style={{ marginBottom: 12, flexShrink: 0 }} />
        <div style={{ flex: 1, minHeight: 0 }}>
          {portfolio.isLoading && <WidgetSkeleton />}
          {!portfolio.isLoading && portfolio.isError && <WidgetSkeleton variant="error" label={title} />}
          {!portfolio.isLoading && !portfolio.isError && <AccountsTable accounts={portfolio.data?.accounts ?? []} />}
        </div>
      </div>
    </WidgetBoundary>
  );
}
